import React, { useEffect, useState } from 'react';
import api from '../api';
import { useAdminLanguage } from '../AdminLanguageContext';

function formatYuan(cents) {
  return `¥ ${(Number(cents || 0) / 100).toFixed(2)}`;
}

function RevenueBars({ rows, empty }) {
  if (!rows?.length) return <div className="mini-chart-empty">{empty}</div>;
  const max = Math.max(...rows.map(row => Number(row.amount || 0)), 1);
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, minHeight: 180 }}>
      {rows.map(row => (
        <div key={row.month} style={{ flex: 1, minWidth: 0, textAlign: 'center' }} title={`${row.month}: ${formatYuan(row.amount)}`}>
          <div style={{
            height: `${Math.max(4, (Number(row.amount || 0) / max) * 140)}px`,
            background: '#0f7f5c',
            borderRadius: '5px 5px 0 0',
          }} />
          <small style={{ color: '#6b7280' }}>{String(row.month).slice(5)}</small>
        </div>
      ))}
    </div>
  );
}

export default function Analytics() {
  const { copy } = useAdminLanguage();
  const dashboardCopy = copy.dashboard;
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/admin/stats')
      .then(response => setStats(response.data))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ padding: 40, color: '#6b7280' }}>{dashboardCopy.loading}</div>;
  if (!stats) return <div style={{ padding: 40, color: '#6b7280' }}>{dashboardCopy.empty}</div>;

  const categoryRanking = stats.categoryRanking || [];
  const maxCategoryAmount = Math.max(...categoryRanking.map(item => Number(item.amount || 0)), 1);

  return (
    <div>
      <div className="page-title">{copy.layout.nav.analytics}</div>
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-body">
            <div className="stat-value">{formatYuan(stats.currentMonthGmv)}</div>
            <div className="stat-label">{dashboardCopy.stats.currentMonthGmv}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-body">
            <div className="stat-value">{formatYuan((stats.revenue12Months || []).reduce((sum, row) => sum + Number(row.amount || 0), 0))}</div>
            <div className="stat-label">{dashboardCopy.charts.revenue12Months}</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-body">
            <div className="stat-value">{stats.topSellers?.length || 0}</div>
            <div className="stat-label">{dashboardCopy.charts.topSellers}</div>
          </div>
        </div>
      </div>

      <div className="charts-row">
        <div className="card chart-card">
          <div className="chart-title">{dashboardCopy.charts.revenue12Months}</div>
          <RevenueBars rows={stats.revenue12Months} empty={dashboardCopy.empty} />
        </div>
        <div className="card dashboard-panel">
          <div className="chart-title">{dashboardCopy.charts.categoryRanking}</div>
          <div className="ranking-list">
            {categoryRanking.map(item => (
              <div className="ranking-row" key={item.category}>
                <div className="ranking-meta">
                  <span>{item.category}</span>
                  <strong>{formatYuan(item.amount)}</strong>
                </div>
                <div className="ranking-track">
                  <span style={{ width: `${(Number(item.amount || 0) / maxCategoryAmount) * 100}%` }} />
                </div>
              </div>
            ))}
            {!categoryRanking.length && <div className="mini-chart-empty">{dashboardCopy.empty}</div>}
          </div>
        </div>
      </div>

      <div className="card dashboard-panel" style={{ marginTop: 16 }}>
        <div className="chart-title">{dashboardCopy.charts.topSellers}</div>
        <div className="dashboard-table-wrap">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>{copy.sellers.columns.seller}</th>
                <th>{copy.sellers.columns.orders}</th>
                <th>{copy.sellers.columns.settlement}</th>
              </tr>
            </thead>
            <tbody>
              {(stats.topSellers || []).map((seller, index) => (
                <tr key={seller.id}>
                  <td className="rank-number">{index + 1}</td>
                  <td>{seller.username}</td>
                  <td>{seller.completedOrders}</td>
                  <td className="seller-money">{formatYuan(seller.sellerSettlement)}</td>
                </tr>
              ))}
              {!stats.topSellers?.length && (
                <tr><td colSpan={4} className="seller-empty">{dashboardCopy.empty}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
